import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { DayStrip } from "@/components/DayStrip";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, Loader2, CheckCircle2, XCircle, Clock, PlaneTakeoff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DEFAULT_SECTIONS } from "@/components/ClassPicker";
import { useSectionsEnabled } from "@/lib/sections";
import { useClassOptions, ClassOptionItems } from "@/components/ClassOptions";

const STATUSES = [
  { id: "Present", color: "bg-emerald-500 hover:bg-emerald-600", light: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  { id: "Absent", color: "bg-rose-500 hover:bg-rose-600", light: "bg-rose-50 text-rose-800 border-rose-200", icon: XCircle },
  { id: "Late", color: "bg-amber-500 hover:bg-amber-600", light: "bg-amber-50 text-amber-800 border-amber-200", icon: Clock },
  { id: "Leave", color: "bg-blue-500 hover:bg-blue-600", light: "bg-blue-50 text-blue-800 border-blue-200", icon: PlaneTakeoff },
];

export default function AttendancePage() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [schoolTypes, setSchoolTypes] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [classOptions, setClassOptions] = useState([]);
  const [className, setClassName] = useState("");
  const [section, setSection] = useState("A");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [markedDays, setMarkedDays] = useState([]);
  const loadDays = () => branchId && className && section && api.get("/attendance/dates", { params: { branch_id: branchId, class_name: className, section } }).then((r) => setMarkedDays(r.data)).catch(() => {});
  useEffect(() => { loadDays(); }, [branchId, className, section]); // eslint-disable-line react-hooks/exhaustive-deps

  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [monthlyReport, setMonthlyReport] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  const isGlobal = user?.role === "master_admin" || user?.role === "super_admin";
  const curBranch = branches.find((b) => b.id === branchId);
  const sectionOptions = curBranch?.sections?.length ? curBranch.sections : DEFAULT_SECTIONS;
  const useSec = useSectionsEnabled();
  const monthlyOpts = useClassOptions(branchId, classOptions);
  const SectionSelect = ({ testid }) => (!useSec ? null :
    <Select value={section} onValueChange={setSection}>
      <SelectTrigger data-testid={testid} className="mt-1.5"><SelectValue placeholder="Section" /></SelectTrigger>
      <SelectContent>{sectionOptions.map((s) => <SelectItem key={s} value={s}>Section {s}</SelectItem>)}</SelectContent>
    </Select>
  );

  useEffect(() => {
    api.get("/branches").then((r) => {
      setBranches(r.data);
      const defBranch = user?.branch_id || r.data[0]?.id || "";
      setBranchId(defBranch);
    });
    api.get("/meta/school-types").then((r) => setSchoolTypes(r.data));
  }, [user]);

  useEffect(() => {
    if (!branchId || schoolTypes.length === 0) return;
    const b = branches.find((x) => x.id === branchId);
    const st = schoolTypes.find((s) => s.id === b?.school_type);
    const classes = st?.classes || [];
    setClassOptions(classes);
    if (classes.length > 0 && !classes.includes(className)) setClassName(classes[0]);
  }, [branchId, branches, schoolTypes]); // eslint-disable-line

  async function loadRoster() {
    if (!branchId || !className) return;
    setLoading(true);
    try {
      const params = { class_name: className, section };
      if (isGlobal) params.branch_id = branchId;
      const [{ data: roster }, { data: existing }] = await Promise.all([
        api.get("/students", { params }),
        api.get("/attendance/daily", { params: { branch_id: branchId, class_name: className, section, date } }),
      ]);
      setStudents(roster);
      const m = {};
      existing.forEach((e) => { m[e.student_id] = e.status; });
      // default remaining to Present
      roster.forEach((s) => { if (!m[s.id]) m[s.id] = "Present"; });
      setMarks(m);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadRoster(); /* eslint-disable-next-line */ }, [branchId, className, section, date]);

  function setAll(status) {
    const m = {};
    students.forEach((s) => { m[s.id] = status; });
    setMarks(m);
  }

  async function saveAttendance() {
    if (students.length === 0) return;
    const b = branches.find((x) => x.id === branchId);
    setSaving(true);
    try {
      const entries = students.map((s) => ({ student_id: s.id, status: marks[s.id] || "Present" }));
      await api.post("/attendance/save", {
        branch_id: branchId,
        school_type: b?.school_type,
        class_name: className,
        section,
        date,
        entries,
      });
      toast.success(`Attendance saved for ${entries.length} students`);
      loadDays();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function loadMonthly() {
    setMonthlyLoading(true);
    try {
      const [y, m] = month.split("-");
      const params = { branch_id: branchId, year: parseInt(y), month: parseInt(m), class_name: className, section };
      const { data } = await api.get("/attendance/monthly", { params });
      setMonthlyReport(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMonthlyLoading(false);
    }
  }

  const summary = useMemo(() => {
    const out = { Present: 0, Absent: 0, Late: 0, Leave: 0 };
    students.forEach((s) => {
      const st = marks[s.id] || "Present";
      out[st] = (out[st] || 0) + 1;
    });
    return out;
  }, [students, marks]);

  const daysInMonth = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m, 0).getDate();
  }, [month]);

  return (
    <AppShell>
      <div className="mb-7">
        <div className="text-xs uppercase tracking-wider font-semibold text-violet-700 mb-2">Daily & Monthly</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Attendance</h1>
        <p className="text-slate-500 mt-1.5">Mark class-wise daily attendance and review monthly performance.</p>
      </div>

      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="bg-violet-100">
          <TabsTrigger value="daily" data-testid="tab-daily-attendance" className="data-[state=active]:bg-white data-[state=active]:text-violet-900">Daily Marker</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly-attendance" className="data-[state=active]:bg-white data-[state=active]:text-violet-900">Monthly Report</TabsTrigger>
        </TabsList>

        {/* Daily */}
        <TabsContent value="daily" className="mt-5">
          <Card className="border-violet-100 mb-5">
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
              {isGlobal && (
                <div>
                  <Label>Branch</Label>
                  <Select value={branchId} onValueChange={setBranchId}>
                    <SelectTrigger data-testid="attendance-branch-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Class</Label>
                <Select value={className} onValueChange={setClassName}>
                  <SelectTrigger data-testid="attendance-class-select" className="mt-1.5"><SelectValue placeholder="Class" /></SelectTrigger>
                  <SelectContent>
                    {classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Section</Label>
                <SectionSelect testid="attendance-section-select" />
              </div>
              <div className="col-span-full">
                <Label>Date</Label>
                <div className="mt-1.5"><DayStrip testid="attendance-date-picker" marked={markedDays} countLabel="absent" badgeTone="bg-rose-500 text-white"
                  preview={(d) => api.get("/attendance/daily", { params: { branch_id: branchId, class_name: className, section, date: d } }).then((r) => {
                    const names = Object.fromEntries(students.map((s) => [s.id, s.name]));
                    const off = r.data.filter((x) => x.status !== "Present");
                    return off.length ? off.map((x) => ({ id: x.student_id, title: names[x.student_id] || x.student_name || "Student", sub: x.status }))
                      : [{ id: "all", title: `All ${r.data.length} students present` }];
                  })} value={date} onChange={(v) => v && setDate(v)} /></div>
              </div>
              <div className="flex items-end">
                <Button
                  data-testid="save-attendance-button"
                  onClick={saveAttendance}
                  disabled={saving || students.length === 0}
                  className="w-full h-10 bg-violet-600 hover:bg-violet-700"
                >
                  {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={16} className="mr-2" /> Save</>}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary + mark all */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {STATUSES.map((s) => (
              <button
                key={s.id}
                onClick={() => setAll(s.id)}
                data-testid={`mark-all-${s.id.toLowerCase()}`}
                className={`${s.light} border rounded-xl p-3 text-left hover:shadow transition`}
              >
                <div className="flex items-center gap-2">
                  <s.icon size={16} />
                  <span className="text-xs uppercase font-semibold tracking-wider">{s.id}</span>
                </div>
                <div className="font-display text-2xl font-bold mt-1">{summary[s.id] || 0}</div>
                <div className="text-[11px] opacity-70 mt-0.5">Tap to mark all</div>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-violet-500" /></div>
          ) : students.length === 0 ? (
            <Card className="border-dashed border-2 border-violet-200">
              <CardContent className="py-16 text-center text-slate-500">
                No students enrolled in {className} Section {section}.
              </CardContent>
            </Card>
          ) : (
            <Card className="border-violet-100">
              <CardContent className="p-0 divide-y divide-slate-100">
                {students.map((s) => (
                  <div key={s.id} data-testid={`attendance-row-${s.admission_number}`} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center text-white font-bold text-sm shrink-0">
                        {s.name.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{s.name}</div>
                        <div className="text-xs text-slate-500 font-mono">{s.admission_number}</div>
                      </div>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {STATUSES.map((st) => {
                        const active = marks[s.id] === st.id;
                        return (
                          <button
                            key={st.id}
                            data-testid={`mark-${s.admission_number}-${st.id.toLowerCase()}`}
                            onClick={() => setMarks({ ...marks, [s.id]: st.id })}
                            className={`px-3 h-9 rounded-lg text-xs font-semibold transition-all ${
                              active ? `${st.color} text-white shadow` : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            {st.id}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Monthly */}
        <TabsContent value="monthly" className="mt-5">
          <Card className="border-violet-100 mb-5">
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
              {isGlobal && (
                <div>
                  <Label>Branch</Label>
                  <Select value={branchId} onValueChange={setBranchId}>
                    <SelectTrigger data-testid="monthly-branch-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Class</Label>
                <Select value={className} onValueChange={setClassName}>
                  <SelectTrigger data-testid="monthly-class-select" className="mt-1.5"><SelectValue placeholder="All classes" /></SelectTrigger>
                  <SelectContent>
                    <ClassOptionItems opts={monthlyOpts} />
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Section</Label>
                <SectionSelect testid="monthly-section-select" />
              </div>
              <div>
                <Label>Month</Label>
                <Input data-testid="monthly-month-picker" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5 input-focus" />
              </div>
              <div className="flex items-end">
                <Button data-testid="run-monthly-report" onClick={loadMonthly} disabled={monthlyLoading} className="w-full h-10 bg-violet-600 hover:bg-violet-700">
                  {monthlyLoading ? <Loader2 className="animate-spin" size={18} /> : "Run Report"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {monthlyReport.length === 0 ? (
            <Card className="border-dashed border-2 border-violet-200">
              <CardContent className="py-16 text-center text-slate-500">
                Run the report for {month} to see per-student attendance across {daysInMonth} days.
              </CardContent>
            </Card>
          ) : (
            <Card className="border-violet-100 overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto scrollbar-thin">
                  <table className="w-full text-sm">
                    <thead className="bg-violet-50 text-slate-700">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold">Student</th>
                        <th className="text-left px-3 py-3 font-semibold">Class</th>
                        <th className="text-center px-3 py-3 font-semibold text-emerald-700">P</th>
                        <th className="text-center px-3 py-3 font-semibold text-rose-700">A</th>
                        <th className="text-center px-3 py-3 font-semibold text-amber-700">L</th>
                        <th className="text-center px-3 py-3 font-semibold text-blue-700">Lv</th>
                        <th className="text-center px-3 py-3 font-semibold">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {monthlyReport.map((r) => {
                        const total = r.present + r.absent + r.late + r.leave;
                        const rate = total ? Math.round(((r.present + r.late) / total) * 100) : 0;
                        return (
                          <tr key={r.student_id} data-testid={`monthly-row-${r.admission_number}`}>
                            <td className="px-4 py-3">
                              <div className="font-semibold text-slate-900">{r.student_name}</div>
                              <div className="text-xs text-slate-500 font-mono">{r.admission_number}</div>
                            </td>
                            <td className="px-3 py-3"><Badge variant="outline">{r.class_name} / {r.section}</Badge></td>
                            <td className="text-center px-3 py-3 text-emerald-700 font-semibold">{r.present}</td>
                            <td className="text-center px-3 py-3 text-rose-700 font-semibold">{r.absent}</td>
                            <td className="text-center px-3 py-3 text-amber-700 font-semibold">{r.late}</td>
                            <td className="text-center px-3 py-3 text-blue-700 font-semibold">{r.leave}</td>
                            <td className="text-center px-3 py-3">
                              <Badge className={rate >= 80 ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : rate >= 60 ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-rose-100 text-rose-800 hover:bg-rose-100"}>
                                {rate}%
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
